import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from '../config/api.config.ts';
import { deviceService } from './device.service.ts';
import { logger } from './logger.service.ts';
import { storageService, StorageKey } from './storage.service.ts';
import { redirectToLogin } from '../utils/navigation.utils.ts';

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json; charset=UTF-8',
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Get device headers from device service
    const deviceHeaders = deviceService.getDeviceHeaders();

    // Add device headers to request
    config.headers['Device-Id'] = deviceHeaders['Device-Id'];
    config.headers['Device-Type'] = deviceHeaders['Device-Type'];

    // Log request headers using logger service
    logger.debug('Request headers being set:', deviceHeaders);

    // Add authorization token if available
    const token = storageService.get<string>(StorageKey.TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Only attempt to refresh token if it's a token expiration issue and not already retrying
      if (storageService.has(StorageKey.REFRESH_TOKEN) && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Import auth service dynamically to avoid circular dependency
          const { authService } = await import('./auth.service');

          // Try to refresh the token
          const refreshData = await authService.refreshToken();

          // If refresh successful, update the Authorization header and retry
          if (refreshData.access_token) {
            // Update the failed request with the new token
            originalRequest.headers.Authorization = `Bearer ${refreshData.access_token}`;
            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          logger.error('Token refresh failed:', refreshError);

          // If refresh fails, clear storage and redirect to login
          storageService.remove(StorageKey.TOKEN);
          storageService.remove(StorageKey.REFRESH_TOKEN);
          storageService.remove(StorageKey.USER);

          // Redirect to login page
          redirectToLogin();
        }
      } else {
        // No refresh token or already tried refresh, redirect to login
        if (!import.meta.env.VITE_DEBUG) {
          storageService.remove(StorageKey.TOKEN);
          storageService.remove(StorageKey.REFRESH_TOKEN);
          storageService.remove(StorageKey.USER);
          redirectToLogin();
        }
      }
    }

    return Promise.reject(error);
  }
);

interface ApiService {
  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
}

export const apiService: ApiService = {
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data, config = {}) => apiClient.post(url, data, config),
  put: (url, data, config = {}) => apiClient.put(url, data, config),
  patch: (url, data, config = {}) => apiClient.patch(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config),
};